"use client";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import { getUserLogout } from "@/redux/user/userSlice";
import { resetAnonymousId } from "@/utils/anonymousId";
import { getApiToken } from "@/redux/config/configSlice";
import { initiateToken } from "@/utils/services/tokenService";
import useCartCount from "@/hooks/useCartCount";
import PartnerAuthModals from "@/components/Partner/PartnerAuthModals";

/**
 * Desktop login / account control for the partner storefront header.
 *
 * PartnerHeader is a server component (kept fully crawlable, zero client JS), so
 * this small interactive island handles auth: a "Login" trigger for guests
 * (opens the shared modals) and, for signed-in users, an "Account" dropdown with
 * links to the dashboard and a "Log Out" action. After authenticating the
 * customer stays on the storefront (see PartnerAuthModals).
 *
 * Logout mirrors the global header (ProfileMenu): clear the redux user state,
 * mint a fresh guest token, refetch the cart against it, then — unlike the
 * global header which sends users to "/" — redirect to the storefront home so
 * the white-label experience isn't broken.
 *
 * `isAuthenticated` comes from redux-persist, which only rehydrates on the
 * client; the server store is always logged-out. We therefore gate the label on
 * a mount flag so the first client render matches the server HTML (guest state)
 * and React doesn't throw a hydration mismatch — the same approach the account
 * pages use. lg-only: mobile uses PartnerMobileBar's Account tab instead.
 */
const PartnerAccountButton = ({ basePath }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const refetchCart = useCartCount();
  const menuRef = useRef();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [loginType, setLoginType] = useState();
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount flag for SSR hydration parity
  useEffect(() => setMounted(true), []);
  const { isAuthenticated } = useSelector((state) => state.user);
  const authed = mounted && isAuthenticated;

  const handleLogout = async () => {
    setShowDropdown(false);
    dispatch(getUserLogout());
    resetAnonymousId();
    // Actually END the session: clear the user JWT immediately, then swap in a
    // fresh guest token — otherwise the never-expiring user JWT keeps riding
    // every request after "logout" (shared-device misattribution + the next
    // login inheriting this account's cart).
    dispatch(getApiToken(""));
    const newToken = await initiateToken();
    if (newToken) {
      dispatch(getApiToken(newToken));
      refetchCart();
    }
    router.push(`${basePath}/`);
  };

  useEffect(() => {
    const closeDropdown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.body.addEventListener("click", closeDropdown);
    return () => document.body.removeEventListener("click", closeDropdown);
  }, []);

  const controlClass =
    "hidden lg:inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-primary-deep transition-colors";

  const icon = (
    <svg width="18" height="18" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M7.5 0C3.36 0 0 3.36 0 7.5C0 11.64 3.36 15 7.5 15C11.64 15 15 11.64 15 7.5C15 3.36 11.64 0 7.5 0ZM4.0125 12.375C4.995 11.67 6.195 11.25 7.5 11.25C8.805 11.25 10.005 11.67 10.9875 12.375C10.005 13.08 8.805 13.5 7.5 13.5C6.195 13.5 4.995 13.08 4.0125 12.375ZM12.105 11.34C10.8375 10.35 9.24 9.75 7.5 9.75C5.76 9.75 4.1625 10.35 2.895 11.34C2.025 10.2975 1.5 8.9625 1.5 7.5C1.5 4.185 4.185 1.5 7.5 1.5C10.815 1.5 13.5 4.185 13.5 7.5C13.5 8.9625 12.975 10.2975 12.105 11.34Z" fill="currentColor"/>
      <path d="M7.5 3C6.0525 3 4.875 4.1775 4.875 5.625C4.875 7.0725 6.0525 8.25 7.5 8.25C8.9475 8.25 10.125 7.0725 10.125 5.625C10.125 4.1775 8.9475 3 7.5 3ZM7.5 6.75C6.8775 6.75 6.375 6.2475 6.375 5.625C6.375 5.0025 6.8775 4.5 7.5 4.5C8.1225 4.5 8.625 5.0025 8.625 5.625C8.625 6.2475 8.1225 6.75 7.5 6.75Z" fill="currentColor"/>
    </svg>
  );

  return (
    <>
      {authed ? (
        <div className="relative hidden lg:block" ref={menuRef}>
          <button
            type="button"
            onClick={() => setShowDropdown((s) => !s)}
            aria-expanded={showDropdown}
            aria-haspopup="menu"
            aria-label="Account menu"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-primary-deep transition-colors"
          >
            {icon}
            Account
          </button>
          {showDropdown && (
            <div
              role="menu"
              className="absolute top-full right-0 mt-2 bg-white py-2 flex flex-col min-w-[180px] rounded-xl z-[60] shadow-[0_10px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.05)]"
            >
              <Link
                href="/user/dashboard"
                role="menuitem"
                onClick={() => setShowDropdown(false)}
                className="px-5 py-2.5 text-sm text-gray-700 hover:text-primary-deep hover:bg-gray-50 transition-colors"
              >
                Account
              </Link>
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="px-5 py-2.5 text-sm text-left text-gray-700 hover:text-primary-deep hover:bg-gray-50 transition-colors bg-transparent border-none cursor-pointer"
              >
                Log Out
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsLoginModalOpen(true)}
          aria-label="Login or sign up"
          aria-haspopup="dialog"
          className={controlClass}
        >
          {icon}
          Login
        </button>
      )}

      <PartnerAuthModals
        isLoginModalOpen={isLoginModalOpen}
        setIsLoginModalOpen={setIsLoginModalOpen}
        isRegisterModalOpen={isRegisterModalOpen}
        setIsRegisterModalOpen={setIsRegisterModalOpen}
        loginType={loginType}
        setLoginType={setLoginType}
        location={`${basePath}/`}
      />
    </>
  );
};

export default PartnerAccountButton;
